import React, { useState, useEffect, useContext } from 'react';
import {
    Button, Text, VStack, Heading, Box, Progress, useToast, Flex, Badge
} from '@chakra-ui/react';
import { FiCheckCircle, FiClock } from 'react-icons/fi';
import attendanceService from '../services/attendanceService';
import AuthContext from '../services/AuthContext';

const AttendancePopup = () => {
    const { user } = useContext(AuthContext);
    const [activeSessions, setActiveSessions] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState({});
    const [markedSessionIds, setMarkedSessionIds] = useState(new Set());
    const toast = useToast();

    useEffect(() => {
        if (!user || user.role !== 'student') {
            setIsOpen(false);
            return;
        }

        const checkForSession = async () => {
            try {
                const result = await attendanceService.checkActiveSession();
                if (result.active && result.sessions) {
                    // Filter out sessions already marked in this instance session
                    const filteredSessions = result.sessions.filter(s => !markedSessionIds.has(s.sessionId));

                    if (filteredSessions.length > 0) {
                        setActiveSessions(filteredSessions);
                        setIsOpen(true);
                    } else {
                        setIsOpen(false);
                    }
                } else {
                    setIsOpen(false);
                }
            } catch (error) {
                console.error('Error checking for attendance session:', error);
            }
        };

        // Initial check
        checkForSession();

        // Poll every 5 seconds
        const interval = setInterval(checkForSession, 5000);

        return () => clearInterval(interval);
        // We include markedSessionIds so if a student marks a session, 
        // the effect can immediately re-filter or wait for next poll.
    }, [user, markedSessionIds]);

    const handleMarkPresent = async (sessionId) => {
        setIsSubmitting(prev => ({ ...prev, [sessionId]: true }));
        try {
            await attendanceService.markPresent(sessionId);
            setMarkedSessionIds(prev => new Set([...prev, sessionId]));
            toast({ title: 'تم تسجيل الحضور بنجاح!', status: 'success' });

            // Remove from local list immediately
            const updated = activeSessions.filter(s => s.sessionId !== sessionId);
            setActiveSessions(updated);

            if (updated.length === 0) {
                setTimeout(() => setIsOpen(false), 1500);
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to mark attendance',
                status: 'error'
            });
            if (error.response?.status === 400) {
                setMarkedSessionIds(prev => new Set([...prev, sessionId]));
            }
        } finally {
            setIsSubmitting(prev => ({ ...prev, [sessionId]: false }));
        }
    };

    if (!isOpen || activeSessions.length === 0) return null;

    return (
        <Box
            position="fixed" top="0" left="0" right="0" bottom="0" zIndex={9999}
            display="flex" alignItems="center" justifyContent="center"
            bg="blackAlpha.800" backdropFilter="blur(10px)" p={4}
        >
            <Box
                bg="white" borderRadius="2xl" boxShadow="2xl" maxW="lg" w="100%"
                p={{ base: 4, md: 8 }} textAlign="center" position="relative"
                animation="slide-up 0.3s ease-out" overflowY="auto" maxH="90vh"
            >
                <VStack spacing={6}>
                    <VStack spacing={2}>
                        <FiClock size={40} color="#F2C919" />
                        <Heading size="lg">تسجيل حضور Zoom</Heading>
                        <Text color="gray.500">لديك {activeSessions.length} حصص تتطلب تسجيل الحضور</Text>
                    </VStack>

                    <VStack spacing={4} w="100%">
                        {activeSessions.map((session) => (
                            <Box
                                key={session.sessionId}
                                w="100%" p={5} border="1px solid"
                                borderColor="gray.100" borderRadius="xl"
                                bg="gray.50"
                            >
                                <Flex justify="space-between" align="center" mb={3} flexWrap="wrap">
                                    <Box textAlign="right">
                                        <Text fontSize="lg" fontWeight="bold">{session.matier}</Text>
                                        <Text fontSize="sm" color="gray.500">المجموعة: {session.group}</Text>
                                    </Box>
                                    <Badge colorScheme="red" p={1} borderRadius="md" fontSize="xs">
                                        ينتهي: {new Date(session.endTime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                    </Badge>
                                </Flex>

                                <Button
                                    colorScheme="yellow"
                                    size="md"
                                    w="100%"
                                    leftIcon={<FiCheckCircle />}
                                    isLoading={isSubmitting[session.sessionId]}
                                    onClick={() => handleMarkPresent(session.sessionId)}
                                    boxShadow="0 2px 10px rgba(242, 201, 25, 0.3)"
                                >
                                    أنا حاضر!
                                </Button>
                            </Box>
                        ))}
                    </VStack>
                </VStack>
            </Box>
            <style>
                {`@keyframes slide-up { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}
            </style>
        </Box>
    );
};

export default AttendancePopup;
